export type HealthGrade = "healthy" | "watch" | "at-risk" | "monitoring";
export type ReasonSeverity = "watch" | "at-risk";
export type ReviewSignalSeverity = "opportunity" | "watch" | "at-risk";
export type AccessValue = "yes" | "no" | "unknown";
export type RpaStatus = "active" | "not_started" | "unknown";
export type OnboardingStage = "target-met" | "early-ramp" | "progressing" | "stalled";
export type DashboardDataSource = "live" | "mock";

export type AccountManagementThresholds = {
  alignmentTargetRate: number;
  alignmentAtRiskRate: number;
  freshnessWatchDays: number;
  freshnessAtRiskDays: number;
  missingMbiWatchRate: number;
  missingMbiAtRiskRate: number;
  healthMinimumPatients: number;
  newClinicGraceDays: number;
  earlyTenureMonths: number;
  icd10CoverageTargetRate: number | null;
  reviewMinimumPatients: number;
  reviewMaxMilestoneGapRate: number;
  reviewMaximumClinics: number;
  reviewPatientAddMinimumBaseline: number;
  reviewPatientAddDeclineRate: number;
  reviewRetellMinimumCalls: number;
  reviewRetellPickupDeclineRate: number;
};

export type ClinicHealthReason = {
  key: string;
  label: string;
  severity: ReasonSeverity;
};

export type ReviewSignal = {
  key: string;
  label: string;
  detail: string;
  severity: ReviewSignalSeverity;
};

export type ClinicProvider = {
  id: string;
  name: string;
  verified: boolean;
  unknown: boolean;
  grouped: boolean;
  patientCount: number;
  alignedPatients: number;
  observedAlignmentRate: number | null;
};

export type AlignedTrendPoint = {
  month: string;
  alignedPatients: number;
};

export type ClinicHealthClinic = {
  id: string;
  name: string;
  state: string;
  emrName: string;
  emrAccess: AccessValue;
  reportAccess: AccessValue;
  rpaStatus: RpaStatus;
  /** Optional only while the separately deployed control plane is on the previous payload. */
  aiCallingApproved?: boolean;
  navigator: string;
  marketablePatients: number;
  totalPatients: number;
  alignedPatients: number;
  alignmentRate: number;
  providerAttributedPatients: number;
  providerCoverageRate: number | null;
  providers: ClinicProvider[];
  medianTenureMonths: number | null;
  clinicAgeDays: number | null;
  latestDcmpDate: string | null;
  freshnessDays: number | null;
  clinicalVisitPatientCount: number;
  missingMbiPatientCount: number;
  missingMbiRate: number | null;
  icd10PatientCount: number;
  icd10CoverageRate: number | null;
  missingIcd10PatientCount: number;
  missingIcd10Rate: number | null;
  patientsAdded30d: number;
  patientsAddedPrior30d: number;
  patientFlowMomentum: number;
  netNewAligned30d: number;
  retellCalls30d: number;
  retellAnswered30d: number;
  retellAnswerRate30d: number | null;
  retellVoicemail30d: number;
  retellVoicemailRate30d: number | null;
  retellIntakesScheduled30d: number;
  retellIntakeAnswerRate30d: number | null;
  retellCallsPrior30d: number;
  retellAnsweredPrior30d: number;
  retellAnswerRatePrior30d: number | null;
  alignedTrend: AlignedTrendPoint[];
  grade: HealthGrade;
  reasons: ClinicHealthReason[];
  needsAttention: boolean;
  hasOpportunity: boolean;
  attentionPriority: boolean;
  opportunityPriority: boolean;
  reviewTargetRate: number | null;
  patientsToReviewTarget: number;
  reviewAttentionScore: number;
  reviewOpportunityScore: number;
  attentionSignals: ReviewSignal[];
  opportunitySignals: ReviewSignal[];
};

export type ClinicHealthSummary = {
  clinicCount: number;
  navigatorCount: number;
  totalPatients: number;
  alignedPatients: number;
  alignedPatientsChange: number;
  alignmentRate: number | null;
  medianTenureMonths: number | null;
  needsAttentionCount: number;
  opportunityCount: number;
  clinicalVisitPatientCount: number;
  clinicalVisitCoverageRate: number | null;
  missingMbiPatientCount: number;
  missingMbiRate: number | null;
  icd10PatientCount: number;
  icd10CoverageRate: number | null;
  missingIcd10PatientCount: number;
  missingIcd10Rate: number | null;
};

export type ClinicHealthResponse = {
  source: DashboardDataSource;
  generatedAt: string;
  asOf: string;
  timezone: string;
  thresholds: AccountManagementThresholds;
  summary: ClinicHealthSummary;
  clinics: ClinicHealthClinic[];
};

export type DataCoverageClinic = {
  id: string;
  name: string;
  state: string;
  emrName: string;
  emrAccess: AccessValue;
  reportAccess: AccessValue;
  navigator: string;
  eligiblePatientCount: number;
  mbiCoveredPatientCount: number;
  missingMbiPatientCount: number;
  mbiCoverageRate: number | null;
  dobCoveredPatientCount: number;
  dobCoverageRate: number | null;
  phoneCoveredPatientCount: number;
  phoneCoverageRate: number | null;
  icd10CoveredPatientCount: number;
  icd10CoverageRate: number | null;
  providerCoveredPatientCount: number;
  providerCoverageRate: number | null;
  npiCoveredPatientCount: number;
  npiCoverageRate: number | null;
  pverifyCoveredPatientCount: number;
  pverifyCoverageRate: number | null;
  allDataCoveredPatientCount: number;
  allDataCoverageRate: number | null;
  isPortfolioTotal: boolean;
};

export type PipelineCoverageClinic = {
  id: string;
  name: string;
  state: string;
  navigator: string;
  eligibleCarespaceCount: number;
  futureAppointmentCount: number;
  futureAppointmentRate: number | null;
  aiAttempt1wCount: number;
  aiAttempt1wRate: number | null;
  aiAttempt2wCount: number;
  aiAttempt2wRate: number | null;
  aiAttempt3wCount: number;
  aiAttempt3wRate: number | null;
  aiAttempt4wCount: number;
  aiAttempt4wRate: number | null;
  untouched4wCount: number;
  untouched4wRate: number | null;
  isPortfolioTotal: boolean;
};

export type PipelineCoverage = {
  clinics: PipelineCoverageClinic[];
};

export type DataCoverageResponse = {
  source: DashboardDataSource;
  generatedAt: string;
  asOf: string;
  timezone: string;
  clinics: DataCoverageClinic[];
  pipeline?: PipelineCoverage;
};

export type OnboardingClinic = {
  id: string;
  name: string;
  state: string;
  totalPatients: number;
  activePipelinePatients: number;
  enrolledPatients: number;
  alignedPatients: number;
  alignmentRate: number;
  patientsToTarget: number;
  alignedLast7d: number;
  alignedLast30d: number;
  patientsAddedLast30d: number;
  firstPatientAddedDate: string | null;
  latestPatientAddedDate: string | null;
  daysActive: number | null;
  stage: OnboardingStage;
  stageLabel: string;
  behindBenchmark: boolean;
  reasons: string[];
};

export type OnboardingSummary = {
  clinicCount: number;
  activeOnboardingCount: number;
  targetMetCount: number;
  stalledCount: number;
  totalPatients: number;
  activePipelinePatients: number;
  enrolledPatients: number;
  alignedPatients: number;
  alignedLast7d: number;
  patientsAddedLast30d: number;
  activePipelineRate: number;
  enrolledRate: number;
  alignmentRate: number;
};

export type RampBandPoint = {
  month: number;
  p25: number;
  p50: number;
  p75: number;
  clinicCount: number;
};

export type RampDailyBandPoint = {
  day: number;
  p25: number;
  p50: number;
  p75: number;
  clinicCount: number;
};

export type RampClinicPoint = {
  month: number;
  snapshotDate: string | null;
  alignmentRate: number;
  marketablePatients: number;
  alignedPatients: number;
  activePatients?: number;
};

export type RampClinicDailyPoint = {
  day: number;
  snapshotDate: string | null;
  alignmentRate: number;
  marketablePatients: number;
  alignedPatients: number;
};

export type RampClinic = {
  id: string;
  name: string;
  state: string;
  launchDate: string | null;
  daysSinceLaunch: number | null;
  behindCurve: boolean;
  points: RampClinicPoint[];
  dailyPoints: RampClinicDailyPoint[];
};

export type OnboardingRamp = {
  anchor: "first-patient-source-date";
  band: RampBandPoint[];
  dailyBand: RampDailyBandPoint[];
  clinics: RampClinic[];
};

export type OnboardingHealthResponse = {
  source: DashboardDataSource;
  generatedAt: string;
  asOf: string;
  timezone: string;
  alignmentTargetRate: number;
  minimumBenchmarkPatientCount: number;
  summary: OnboardingSummary;
  clinics: OnboardingClinic[];
  ramp: OnboardingRamp;
};

export type AccountManagementSnapshotResponse = {
  clinicHealth: ClinicHealthResponse;
  dataCoverage: DataCoverageResponse;
  onboardingHealth: OnboardingHealthResponse;
};

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function nullableFiniteNumber(value: unknown): boolean {
  return value === null || finiteNumber(value);
}

function validReason(reason: unknown): boolean {
  if (!reason || typeof reason !== "object") return false;
  const item = reason as Partial<ClinicHealthReason>;
  return (
    typeof item.key === "string" &&
    typeof item.label === "string" &&
    (item.severity === "watch" || item.severity === "at-risk")
  );
}

function validTrendPoint(point: unknown): boolean {
  if (!point || typeof point !== "object") return false;
  const item = point as Partial<AlignedTrendPoint>;
  return (
    typeof item.month === "string" &&
    /^\d{4}-\d{2}$/.test(item.month) &&
    finiteNumber(item.alignedPatients)
  );
}

function validProvider(provider: unknown): boolean {
  if (!provider || typeof provider !== "object") return false;
  const item = provider as Partial<ClinicProvider>;
  return (
    typeof item.id === "string" &&
    typeof item.name === "string" &&
    typeof item.verified === "boolean" &&
    typeof item.unknown === "boolean" &&
    typeof item.grouped === "boolean" &&
    finiteNumber(item.patientCount) &&
    finiteNumber(item.alignedPatients) &&
    nullableFiniteNumber(item.observedAlignmentRate)
  );
}

function validReviewSignal(signal: unknown): boolean {
  if (!signal || typeof signal !== "object") return false;
  const item = signal as Partial<ReviewSignal>;
  return (
    typeof item.key === "string" &&
    typeof item.label === "string" &&
    typeof item.detail === "string" &&
    (item.severity === "opportunity" || item.severity === "watch" || item.severity === "at-risk")
  );
}

function validThresholds(thresholds: unknown): thresholds is AccountManagementThresholds {
  if (!thresholds || typeof thresholds !== "object") return false;
  const item = thresholds as Partial<AccountManagementThresholds>;
  const numericKeys = [
    "alignmentTargetRate",
    "alignmentAtRiskRate",
    "freshnessWatchDays",
    "freshnessAtRiskDays",
    "missingMbiWatchRate",
    "missingMbiAtRiskRate",
    "healthMinimumPatients",
    "newClinicGraceDays",
  ] as const;
  return (
    numericKeys.every((key) => finiteNumber(item[key])) &&
    Number.isInteger(item.reviewMaximumClinics) &&
    (item.reviewMaximumClinics as number) >= 1 &&
    (item.reviewMaximumClinics as number) <= 5
  );
}

function validClinic(clinic: unknown): clinic is ClinicHealthClinic {
  if (!clinic || typeof clinic !== "object") return false;
  const item = clinic as Partial<ClinicHealthClinic>;
  return (
    typeof item.id === "string" &&
    typeof item.name === "string" &&
    typeof item.state === "string" &&
    typeof item.emrName === "string" &&
    (item.emrAccess === "yes" || item.emrAccess === "no" || item.emrAccess === "unknown") &&
    (item.reportAccess === "yes" ||
      item.reportAccess === "no" ||
      item.reportAccess === "unknown") &&
    typeof item.emrName === "string" &&
    (item.emrAccess === "yes" || item.emrAccess === "no" || item.emrAccess === "unknown") &&
    (item.reportAccess === "yes" ||
      item.reportAccess === "no" ||
      item.reportAccess === "unknown") &&
    (item.rpaStatus === "active" ||
      item.rpaStatus === "not_started" ||
      item.rpaStatus === "unknown") &&
    (item.aiCallingApproved === undefined || typeof item.aiCallingApproved === "boolean") &&
    typeof item.navigator === "string" &&
    finiteNumber(item.totalPatients) &&
    finiteNumber(item.alignedPatients) &&
    finiteNumber(item.alignmentRate) &&
    finiteNumber(item.providerAttributedPatients) &&
    nullableFiniteNumber(item.providerCoverageRate) &&
    Array.isArray(item.providers) &&
    item.providers.every(validProvider) &&
    finiteNumber(item.clinicalVisitPatientCount) &&
    finiteNumber(item.missingMbiPatientCount) &&
    finiteNumber(item.icd10PatientCount) &&
    finiteNumber(item.missingIcd10PatientCount) &&
    finiteNumber(item.patientsAdded30d) &&
    finiteNumber(item.patientsAddedPrior30d) &&
    finiteNumber(item.patientFlowMomentum) &&
    finiteNumber(item.netNewAligned30d ?? 0) &&
    nullableFiniteNumber(item.medianTenureMonths) &&
    nullableFiniteNumber(item.clinicAgeDays) &&
    nullableFiniteNumber(item.freshnessDays) &&
    nullableFiniteNumber(item.missingMbiRate) &&
    nullableFiniteNumber(item.icd10CoverageRate) &&
    nullableFiniteNumber(item.missingIcd10Rate) &&
    finiteNumber(item.retellCalls30d) &&
    finiteNumber(item.retellAnswered30d) &&
    nullableFiniteNumber(item.retellAnswerRate30d) &&
    finiteNumber(item.retellVoicemail30d) &&
    nullableFiniteNumber(item.retellVoicemailRate30d) &&
    finiteNumber(item.retellIntakesScheduled30d) &&
    nullableFiniteNumber(item.retellIntakeAnswerRate30d) &&
    finiteNumber(item.retellCallsPrior30d) &&
    finiteNumber(item.retellAnsweredPrior30d) &&
    nullableFiniteNumber(item.retellAnswerRatePrior30d) &&
    typeof item.needsAttention === "boolean" &&
    typeof item.hasOpportunity === "boolean" &&
    typeof item.attentionPriority === "boolean" &&
    typeof item.opportunityPriority === "boolean" &&
    nullableFiniteNumber(item.reviewTargetRate) &&
    finiteNumber(item.patientsToReviewTarget) &&
    finiteNumber(item.reviewAttentionScore) &&
    finiteNumber(item.reviewOpportunityScore) &&
    Array.isArray(item.attentionSignals) &&
    item.attentionSignals.every(validReviewSignal) &&
    Array.isArray(item.opportunitySignals) &&
    item.opportunitySignals.every(validReviewSignal) &&
    (item.grade === "healthy" ||
      item.grade === "watch" ||
      item.grade === "at-risk" ||
      item.grade === "monitoring") &&
    Array.isArray(item.reasons) &&
    item.reasons.every(validReason) &&
    Array.isArray(item.alignedTrend) &&
    item.alignedTrend.every(validTrendPoint)
  );
}

function validRampPoint(point: unknown): boolean {
  if (!point || typeof point !== "object") return false;
  const item = point as Partial<RampClinicPoint>;
  return (
    finiteNumber(item.month) &&
    typeof item.snapshotDate === "string" &&
    finiteNumber(item.alignmentRate) &&
    finiteNumber(item.marketablePatients ?? item.activePatients) &&
    finiteNumber(item.alignedPatients)
  );
}

function validRampBandPoint(point: unknown): boolean {
  if (!point || typeof point !== "object") return false;
  const item = point as Partial<RampBandPoint>;
  return (
    finiteNumber(item.month) &&
    finiteNumber(item.p25) &&
    finiteNumber(item.p50) &&
    finiteNumber(item.p75) &&
    finiteNumber(item.clinicCount) &&
    (item.p25 as number) <= (item.p50 as number) &&
    (item.p50 as number) <= (item.p75 as number)
  );
}

function validRampDailyPoint(point: unknown): boolean {
  if (!point || typeof point !== "object") return false;
  const item = point as Partial<RampClinicDailyPoint>;
  return (
    finiteNumber(item.day) &&
    typeof item.snapshotDate === "string" &&
    finiteNumber(item.alignmentRate) &&
    finiteNumber(item.marketablePatients) &&
    finiteNumber(item.alignedPatients)
  );
}

function validRampDailyBandPoint(point: unknown): boolean {
  if (!point || typeof point !== "object") return false;
  const item = point as Partial<RampDailyBandPoint>;
  return (
    finiteNumber(item.day) &&
    finiteNumber(item.p25) &&
    finiteNumber(item.p50) &&
    finiteNumber(item.p75) &&
    finiteNumber(item.clinicCount) &&
    item.p25 <= item.p50 &&
    item.p50 <= item.p75
  );
}

function validRampClinic(clinic: unknown): boolean {
  if (!clinic || typeof clinic !== "object") return false;
  const item = clinic as Partial<RampClinic>;
  return (
    typeof item.id === "string" &&
    typeof item.name === "string" &&
    typeof item.state === "string" &&
    typeof item.launchDate === "string" &&
    nullableFiniteNumber(item.daysSinceLaunch) &&
    typeof item.behindCurve === "boolean" &&
    Array.isArray(item.points) &&
    item.points.every(validRampPoint) &&
    Array.isArray(item.dailyPoints) &&
    item.dailyPoints.every(validRampDailyPoint) &&
    (item.points.length > 0 || item.dailyPoints.length > 0)
  );
}

function validOnboardingClinic(clinic: unknown): clinic is OnboardingClinic {
  if (!clinic || typeof clinic !== "object") return false;
  const item = clinic as Partial<OnboardingClinic>;
  return (
    typeof item.id === "string" &&
    typeof item.name === "string" &&
    typeof item.state === "string" &&
    finiteNumber(item.totalPatients) &&
    finiteNumber(item.activePipelinePatients) &&
    finiteNumber(item.enrolledPatients) &&
    finiteNumber(item.alignedPatients) &&
    finiteNumber(item.alignmentRate) &&
    finiteNumber(item.patientsToTarget) &&
    finiteNumber(item.alignedLast7d) &&
    finiteNumber(item.alignedLast30d) &&
    finiteNumber(item.patientsAddedLast30d) &&
    nullableFiniteNumber(item.daysActive) &&
    (item.stage === "target-met" ||
      item.stage === "early-ramp" ||
      item.stage === "progressing" ||
      item.stage === "stalled") &&
    typeof item.stageLabel === "string" &&
    typeof item.behindBenchmark === "boolean" &&
    Array.isArray(item.reasons) &&
    item.reasons.every((reason) => typeof reason === "string")
  );
}

export function isClinicHealthResponse(value: unknown): value is ClinicHealthResponse {
  if (!value || typeof value !== "object") return false;
  const data = value as Partial<ClinicHealthResponse>;
  return (
    (data.source === "live" || data.source === "mock") &&
    typeof data.generatedAt === "string" &&
    typeof data.asOf === "string" &&
    validThresholds(data.thresholds) &&
    !!data.summary &&
    typeof data.summary === "object" &&
    finiteNumber(data.summary.clinicCount) &&
    finiteNumber(data.summary.totalPatients) &&
    finiteNumber(data.summary.alignedPatients) &&
    finiteNumber(data.summary.alignedPatientsChange) &&
    finiteNumber(data.summary.alignmentRate) &&
    finiteNumber(data.summary.navigatorCount) &&
    finiteNumber(data.summary.needsAttentionCount) &&
    finiteNumber(data.summary.opportunityCount) &&
    finiteNumber(data.summary.clinicalVisitPatientCount) &&
    finiteNumber(data.summary.missingMbiPatientCount) &&
    finiteNumber(data.summary.icd10PatientCount) &&
    finiteNumber(data.summary.missingIcd10PatientCount) &&
    nullableFiniteNumber(data.summary.medianTenureMonths) &&
    nullableFiniteNumber(data.summary.clinicalVisitCoverageRate) &&
    nullableFiniteNumber(data.summary.missingMbiRate) &&
    nullableFiniteNumber(data.summary.icd10CoverageRate) &&
    nullableFiniteNumber(data.summary.missingIcd10Rate) &&
    Array.isArray(data.clinics) &&
    data.clinics.every(validClinic)
  );
}

function validDataCoverageClinic(value: unknown): value is DataCoverageClinic {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<DataCoverageClinic>;
  return (
    typeof item.id === "string" &&
    typeof item.name === "string" &&
    typeof item.state === "string" &&
    typeof item.emrName === "string" &&
    (item.emrAccess === "yes" || item.emrAccess === "no" || item.emrAccess === "unknown") &&
    (item.reportAccess === "yes" ||
      item.reportAccess === "no" ||
      item.reportAccess === "unknown") &&
    typeof item.navigator === "string" &&
    finiteNumber(item.eligiblePatientCount) &&
    finiteNumber(item.mbiCoveredPatientCount) &&
    finiteNumber(item.missingMbiPatientCount) &&
    finiteNumber(item.dobCoveredPatientCount) &&
    finiteNumber(item.phoneCoveredPatientCount) &&
    finiteNumber(item.icd10CoveredPatientCount) &&
    finiteNumber(item.providerCoveredPatientCount) &&
    finiteNumber(item.npiCoveredPatientCount) &&
    finiteNumber(item.pverifyCoveredPatientCount) &&
    finiteNumber(item.allDataCoveredPatientCount) &&
    nullableFiniteNumber(item.mbiCoverageRate) &&
    nullableFiniteNumber(item.dobCoverageRate) &&
    nullableFiniteNumber(item.phoneCoverageRate) &&
    nullableFiniteNumber(item.icd10CoverageRate) &&
    nullableFiniteNumber(item.providerCoverageRate) &&
    nullableFiniteNumber(item.npiCoverageRate) &&
    nullableFiniteNumber(item.pverifyCoverageRate) &&
    nullableFiniteNumber(item.allDataCoverageRate) &&
    typeof item.isPortfolioTotal === "boolean"
  );
}

function validPipelineCoverageClinic(value: unknown): value is PipelineCoverageClinic {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<PipelineCoverageClinic>;
  return (
    typeof item.id === "string" &&
    typeof item.name === "string" &&
    typeof item.state === "string" &&
    typeof item.navigator === "string" &&
    finiteNumber(item.eligibleCarespaceCount) &&
    finiteNumber(item.futureAppointmentCount) &&
    nullableFiniteNumber(item.futureAppointmentRate) &&
    finiteNumber(item.aiAttempt1wCount) &&
    nullableFiniteNumber(item.aiAttempt1wRate) &&
    finiteNumber(item.aiAttempt2wCount) &&
    nullableFiniteNumber(item.aiAttempt2wRate) &&
    finiteNumber(item.aiAttempt3wCount) &&
    nullableFiniteNumber(item.aiAttempt3wRate) &&
    finiteNumber(item.aiAttempt4wCount) &&
    nullableFiniteNumber(item.aiAttempt4wRate) &&
    finiteNumber(item.untouched4wCount) &&
    nullableFiniteNumber(item.untouched4wRate) &&
    typeof item.isPortfolioTotal === "boolean"
  );
}

function validPipelineCoverage(value: unknown): value is PipelineCoverage {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<PipelineCoverage>;
  return Array.isArray(item.clinics) && item.clinics.every(validPipelineCoverageClinic);
}

export function isDataCoverageResponse(value: unknown): value is DataCoverageResponse {
  if (!value || typeof value !== "object") return false;
  const data = value as Partial<DataCoverageResponse>;
  return (
    (data.source === "live" || data.source === "mock") &&
    typeof data.generatedAt === "string" &&
    typeof data.asOf === "string" &&
    typeof data.timezone === "string" &&
    Array.isArray(data.clinics) &&
    data.clinics.every(validDataCoverageClinic) &&
    (data.pipeline === undefined || validPipelineCoverage(data.pipeline))
  );
}

export function isOnboardingHealthResponse(value: unknown): value is OnboardingHealthResponse {
  if (!value || typeof value !== "object") return false;
  const data = value as Partial<OnboardingHealthResponse>;
  return (
    (data.source === "live" || data.source === "mock") &&
    typeof data.generatedAt === "string" &&
    typeof data.asOf === "string" &&
    finiteNumber(data.alignmentTargetRate) &&
    finiteNumber(data.minimumBenchmarkPatientCount) &&
    !!data.summary &&
    finiteNumber(data.summary.clinicCount) &&
    finiteNumber(data.summary.totalPatients) &&
    finiteNumber(data.summary.activePipelinePatients) &&
    finiteNumber(data.summary.enrolledPatients) &&
    finiteNumber(data.summary.alignedPatients) &&
    finiteNumber(data.summary.activePipelineRate) &&
    finiteNumber(data.summary.enrolledRate) &&
    finiteNumber(data.summary.alignmentRate) &&
    !!data.ramp &&
    data.ramp.anchor === "first-patient-source-date" &&
    Array.isArray(data.ramp.band) &&
    data.ramp.band.every(validRampBandPoint) &&
    Array.isArray(data.ramp.dailyBand) &&
    data.ramp.dailyBand.every(validRampDailyBandPoint) &&
    Array.isArray(data.ramp.clinics) &&
    data.ramp.clinics.every(validRampClinic) &&
    Array.isArray(data.clinics) &&
    data.clinics.every(validOnboardingClinic)
  );
}

export function isAccountManagementSnapshotResponse(
  value: unknown,
): value is AccountManagementSnapshotResponse {
  if (!value || typeof value !== "object") return false;
  const data = value as Partial<AccountManagementSnapshotResponse>;
  return (
    isClinicHealthResponse(data.clinicHealth) &&
    isDataCoverageResponse(data.dataCoverage) &&
    isOnboardingHealthResponse(data.onboardingHealth)
  );
}
